import React from "react";
import { SEO } from "../../seo/SEO";
import styled from "styled-components";
import { useTypedResource } from "../../i18n/languagehandler";

const CAPTIVATE_URL = "https://tilknytning.captivate.fm/";
const SPOTIFY_URL = "https://open.spotify.com/show/1GO4DF9tfdISLqryjTrv0g";

const Section = styled.section`
  max-width: 1100px;
  margin: 48px auto 80px;
  padding: 0 20px;
`;

const Hero = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 1.15fr) 1fr;
  gap: 40px;
  align-items: center;
  background: #fff;
  border-radius: 24px;
  padding: 28px;
  box-shadow: 0 18px 50px rgba(31, 45, 61, 0.08);

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    padding: 18px;
    gap: 24px;
  }
`;

const Artwork = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 16px;
  box-shadow: 0 16px 36px rgba(31, 45, 61, 0.16);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Kicker = styled.p`
  color: var(--primary);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0 0 8px 0;
`;

const Title = styled.h2`
  color: var(--text);
  font-size: 40px;
  margin: 0 0 8px 0;
  line-height: 1.1;

  @media (max-width: 800px) {
    font-size: 32px;
  }
`;

const Hosts = styled.p`
  color: var(--text);
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 22px 0;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0 0 24px 0;
`;

const ListenButton = styled.a`
  background: var(--primary);
  color: #fff;
  text-decoration: none;
  padding: 14px 22px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 8px 20px rgba(55, 90, 127, 0.22);

  &:hover {
    background: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(55, 90, 127, 0.3);
  }
`;

const SpotifyButton = styled(ListenButton)`
  background: #fff;
  color: var(--primary);
  border: 2px solid var(--primary);
  box-shadow: none;

  &:hover {
    background: var(--primary);
    color: #fff;
  }
`;

const Body = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.7;
  margin: 0 0 14px 0;
`;

const EpisodeBlock = styled.div`
  margin-top: 28px;
  background: #fff;
  border-radius: 24px;
  padding: 8px 28px 12px;
  box-shadow: 0 18px 50px rgba(31, 45, 61, 0.08);

  @media (max-width: 800px) {
    padding: 8px 18px 12px;
  }
`;

const EpisodeHeading = styled.h3`
  color: var(--text);
  font-size: 24px;
  margin: 20px 0 4px 0;
`;

const Episode = styled.article`
  padding: 18px 0;
  border-top: 1px solid rgba(55, 90, 127, 0.12);
`;

const EpisodeTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: baseline;
`;

const EpisodeTitle = styled.h4`
  color: var(--text);
  font-size: 18px;
  margin: 0;
  line-height: 1.35;
`;

const Duration = styled.span`
  color: var(--primary);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
`;

const EpisodeText = styled.p`
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.65;
  margin: 8px 0 0 0;
`;

export const Podcast: React.FC = () => {
  const res = useTypedResource<{
    title: string;
    kicker: string;
    hosts: string;
    captivate: string;
    spotify: string;
    paragraph1: string;
    paragraph2: string;
    paragraph3: string;
    episodesHeading: string;
    episodes: { title: string; duration: string; description: string }[];
  }>("podcast");

  const episodes = Array.isArray(res.episodes) ? res.episodes : [];

  return (
    <main>
      <SEO
        title={res.title}
        description={res.paragraph1}
        path="/podcast"
        image="/assets/Podcast/tilknytning.jpg"
      />
      <Section>
        <Hero>
          <Artwork
            src={process.env.PUBLIC_URL + "/assets/Podcast/tilknytning.jpg"}
            alt={res.title}
          />
          <Content>
            <Kicker>{res.kicker}</Kicker>
            <Title>{res.title}</Title>
            <Hosts>{res.hosts}</Hosts>
            <Actions>
              <ListenButton
                href={CAPTIVATE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {res.captivate}
              </ListenButton>
              <SpotifyButton
                href={SPOTIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {res.spotify}
              </SpotifyButton>
            </Actions>
            <Body>{res.paragraph1}</Body>
            <Body>{res.paragraph2}</Body>
            <Body>{res.paragraph3}</Body>
          </Content>
        </Hero>
        <EpisodeBlock>
          <EpisodeHeading>{res.episodesHeading}</EpisodeHeading>
          {episodes.map((episode) => (
            <Episode key={episode.title}>
              <EpisodeTop>
                <EpisodeTitle>{episode.title}</EpisodeTitle>
                <Duration>{episode.duration}</Duration>
              </EpisodeTop>
              <EpisodeText>{episode.description}</EpisodeText>
            </Episode>
          ))}
        </EpisodeBlock>
      </Section>
    </main>
  );
};
